import { useUser } from "apps/web/src/utils/tokens";
import React, { useState } from "react";
import { trackEvent } from "apps/web/src/reporting/reporting";
import {
  EditRecipeFullInput,
  EditRecipePartialInput,
  useEditRecipeAllergensMutation,
  useEditRecipeFullMutation,
  useEditRecipePartialMutation,
  useRecipeEditDialogQuery,
} from "../../../types";
import { useSnackbar } from "../../Snackbar/SnackbarContext";
import LoadingBackdrop from "../../universal/LoadingBackdrop";
import { RecipeFormState } from "../Form/utils";
import { RecipeAllergensChange, RecipeEditContent } from "./RecipeEditContent";

interface RecipeEditProps {
  onClose: () => void;
  onEdit: () => void;
  recipeId: string;
  onDuplicate: () => void;
  forStaff?: boolean;
  readonly?: boolean;
  onScaleAndCreateNewRecipe: (state: RecipeFormState) => void;
}

export const RecipeEdit = ({ onClose, onEdit, recipeId, onDuplicate, forStaff, readonly, onScaleAndCreateNewRecipe }: RecipeEditProps) => {
  const user = useUser();
  const { setMessage } = useSnackbar();
  const { data, loading } = useRecipeEditDialogQuery({
    variables: {
      id: recipeId,
    },
  });

  const isOrgGroupReadonly = Boolean(data?.recipe?.orgGroup) && Boolean(user) && user?.id !== data?.recipe.owner.id;

  const [editRecipeFull] = useEditRecipeFullMutation();
  const [editRecipePartial] = useEditRecipePartialMutation();
  const [editRecipeAllergens] = useEditRecipeAllergensMutation();
  const [saving, setSaving] = useState(false);

  // Allergens are saved in place on the recipe that results from the main edit, which is a new version after a full edit
  const save = async (edit: () => Promise<string | undefined>, allergens?: RecipeAllergensChange) => {
    setSaving(true);
    let savedId: string | undefined;
    try {
      savedId = await edit();
    } catch {
      // Errors are reported by the Apollo error link
    }
    if (savedId && allergens) {
      try {
        await editRecipeAllergens({ variables: { input: { id: savedId, selfAllergens: allergens.selfAllergens } } });
      } catch {
        setMessage("error", "The recipe was saved but its allergens could not be saved");
      }
    }
    setSaving(false);
    if (savedId) {
      trackEvent("nutrition_recipe_edited", {});
      onEdit();
    }
  };

  const onEditFull = (input: EditRecipeFullInput, allergens?: RecipeAllergensChange) =>
    save(async () => (await editRecipeFull({ variables: { input } })).data?.editRecipeFull.recipe.id, allergens);

  const onEditPartial = (input: EditRecipePartialInput, allergens?: RecipeAllergensChange) =>
    save(async () => (await editRecipePartial({ variables: { input } })).data?.editRecipePartial.recipe.id, allergens);

  const isLoading = saving || loading || !data;

  return isLoading ? (
    <LoadingBackdrop open={isLoading} onClose={onClose} />
  ) : (
    <RecipeEditContent
      onClose={onClose}
      onEditPartial={onEditPartial}
      onEditFull={onEditFull}
      recipe={data.recipe}
      onDuplicate={onDuplicate}
      forStaff={forStaff}
      readonly={readonly || isOrgGroupReadonly}
      onScaleAndCreateNewRecipe={onScaleAndCreateNewRecipe}
    />
  );
};
